import Config from '@config';
import { Dispatch } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  Contract,
  ContractDetails,
  ContractPlannedVolumeMonth,
  CreatePeriodPlannedVolumeParams,
  DistributePlannedVolumeParams,
  GetContractActsParams,
  GetContractActsResult,
  GetContractDetailsParams,
  GetContractMeteringPointPlannedVolumesParams,
  GetContractMeteringPointPlannedVolumesResult,
  GetContractPlannedVolumesParams,
  GetContractsParams,
  GetMeteringPointDailyPlannedVolumesParams,
  GetMeteringPointDailyPlannedVolumesResult,
  RequestPlannedVolumeUpdateParams,
} from '@/features/contracts/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

const PLANNED_VOLUME_TAGS = [
  'PlannedVolumeMeteringPoints',
  'PlannedVolumeDaily',
  'Contract',
] as const;

// Planned-volume writes are only *requests*: SBA publishes them to Supply, and the
// local mirror catches up once Supply's own event round-trips back. Refetching right
// after the POST would still show the old values, so refetch again a little later.
const MIRROR_SYNC_REFETCH_DELAYS_MS = [1500, 5000];

const refetchPlannedVolumesAfterSync = async (
  dispatch: Dispatch,
  queryFulfilled: Promise<unknown>,
) => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }

  MIRROR_SYNC_REFETCH_DELAYS_MS.forEach(delay =>
    setTimeout(
      () =>
        dispatch(contractsApi.util.invalidateTags([...PLANNED_VOLUME_TAGS])),
      delay,
    ),
  );
};

export const contractsApi = createApi({
  reducerPath: 'contractsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/contracts',
  }),
  tagTypes: [
    'Contract',
    'PlannedVolumeMeteringPoints',
    'PlannedVolumeDaily',
    'Act',
  ],
  endpoints: builder => ({
    getContracts: builder.query<Contract[], GetContractsParams>({
      query: params => ({
        url: '',
        params,
      }),
      providesTags: ['Contract'],
    }),
    getContractDetails: builder.query<
      ContractDetails,
      GetContractDetailsParams
    >({
      query: ({ id, ...params }) => ({
        url: `/${id}`,
        params,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Contract', id }],
    }),
    getContractPlannedVolumes: builder.query<
      { months: ContractPlannedVolumeMonth[] },
      GetContractPlannedVolumesParams
    >({
      query: ({ id, ...params }) => ({
        url: `/${id}/planned-volumes`,
        params,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: 'Contract', id: `${id}-planned-volumes` },
      ],
    }),
    getContractMeteringPointPlannedVolumes: builder.query<
      GetContractMeteringPointPlannedVolumesResult,
      GetContractMeteringPointPlannedVolumesParams
    >({
      query: ({ id, ...params }) => ({
        url: `/${id}/planned-volumes/metering-points`,
        params,
      }),
      providesTags: (_result, _error, { id, year, month }) => [
        { type: 'PlannedVolumeMeteringPoints', id: `${id}-${year}-${month}` },
      ],
    }),
    getMeteringPointDailyPlannedVolumes: builder.query<
      GetMeteringPointDailyPlannedVolumesResult,
      GetMeteringPointDailyPlannedVolumesParams
    >({
      query: ({ id, meteringPointId, ...params }) => ({
        url: `/${id}/planned-volumes/metering-points/${meteringPointId}/daily`,
        params,
      }),
      providesTags: (_result, _error, { id, meteringPointId, year, month }) => [
        {
          type: 'PlannedVolumeDaily',
          id: `${id}-${meteringPointId}-${year}-${month}`,
        },
      ],
    }),
    getContractActs: builder.query<
      GetContractActsResult,
      GetContractActsParams
    >({
      query: ({ id, ...params }) => ({
        url: `/${id}/acts`,
        params,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Act', id }],
    }),
    requestPlannedVolumeUpdate: builder.mutation<
      void,
      RequestPlannedVolumeUpdateParams
    >({
      query: ({ id, organizationId, meteringPoints }) => ({
        url: `/${id}/planned-volumes/request-update`,
        method: 'POST',
        params: { organizationId },
        // `previous` is frontend-only (drives the optimistic patch below) — strip it from the wire payload.
        data: {
          meteringPoints: meteringPoints.map(({ meteringPointId, days }) => ({
            meteringPointId,
            days: days.map(({ date, volume }) => ({ date, volume })),
          })),
        },
      }),
      // This request only asks Supply (the source of truth) to apply the change — the mirror this
      // app reads from is updated later, async, once Supply's response event round-trips back over
      // RabbitMQ. Refetching on success would just re-read the not-yet-updated mirror, so we patch
      // the cache directly from what we know we just submitted instead of invalidating tags.
      async onQueryStarted(
        { id, organizationId, meteringPoints },
        { dispatch, queryFulfilled },
      ) {
        const patches: { undo: () => void }[] = [];
        const monthDeltaByMeteringPoint = new Map<
          string,
          Map<string, number>
        >();
        const monthDeltaTotal = new Map<string, number>();

        for (const { meteringPointId, days } of meteringPoints) {
          const monthDeltas =
            monthDeltaByMeteringPoint.get(meteringPointId) ??
            new Map<string, number>();

          for (const { date, volume, previous } of days) {
            const [year, month] = date.split('-').map(Number);
            const monthKey = `${year}-${month}`;
            const delta = volume - previous;

            monthDeltas.set(monthKey, (monthDeltas.get(monthKey) ?? 0) + delta);
            monthDeltaTotal.set(
              monthKey,
              (monthDeltaTotal.get(monthKey) ?? 0) + delta,
            );

            patches.push(
              dispatch(
                contractsApi.util.updateQueryData(
                  'getMeteringPointDailyPlannedVolumes',
                  { id, meteringPointId, organizationId, year, month },
                  draft => {
                    const item = draft.items.find(x => x.date === date);
                    if (item) {
                      item.volume = volume;
                    } else {
                      draft.items.push({ date, volume });
                    }
                  },
                ),
              ),
            );
          }

          monthDeltaByMeteringPoint.set(meteringPointId, monthDeltas);
        }

        for (const [
          meteringPointId,
          monthDeltas,
        ] of monthDeltaByMeteringPoint) {
          for (const [monthKey, delta] of monthDeltas) {
            const [year, month] = monthKey.split('-').map(Number);

            patches.push(
              dispatch(
                contractsApi.util.updateQueryData(
                  'getContractMeteringPointPlannedVolumes',
                  { id, organizationId, year, month },
                  draft => {
                    const row = draft.items.find(
                      x => x.meteringPointId === meteringPointId,
                    );
                    if (!row) return;

                    row.volume += delta;
                    draft.totalVolume += delta;
                  },
                ),
              ),
            );
          }
        }

        const yearsTouched = new Set(
          [...monthDeltaTotal.keys()].map(monthKey =>
            Number(monthKey.split('-')[0]),
          ),
        );

        for (const year of yearsTouched) {
          patches.push(
            dispatch(
              contractsApi.util.updateQueryData(
                'getContractPlannedVolumes',
                { id, organizationId, year },
                draft => {
                  for (const [monthKey, delta] of monthDeltaTotal) {
                    const [rowYear, rowMonth] = monthKey.split('-').map(Number);
                    if (rowYear !== year) continue;

                    const row = draft.months.find(x => x.month === rowMonth);
                    if (row) {
                      row.volume += delta;
                    } else {
                      draft.months.push({ month: rowMonth, volume: delta });
                    }
                  }
                },
              ),
            ),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
    }),
    createPeriodPlannedVolume: builder.mutation<
      void,
      CreatePeriodPlannedVolumeParams
    >({
      query: ({ id, organizationId, ...data }) => ({
        url: `/${id}/planned-volumes/period`,
        method: 'POST',
        params: { organizationId },
        data,
      }),
      invalidatesTags: [...PLANNED_VOLUME_TAGS],
      onQueryStarted: (_args, { dispatch, queryFulfilled }) =>
        refetchPlannedVolumesAfterSync(dispatch, queryFulfilled),
    }),
    distributePlannedVolume: builder.mutation<
      void,
      DistributePlannedVolumeParams
    >({
      query: ({ id, organizationId, ...data }) => ({
        url: `/${id}/planned-volumes/distribute`,
        method: 'POST',
        params: { organizationId },
        data,
      }),
      invalidatesTags: [...PLANNED_VOLUME_TAGS],
      onQueryStarted: (_args, { dispatch, queryFulfilled }) =>
        refetchPlannedVolumesAfterSync(dispatch, queryFulfilled),
    }),
  }),
});

export const {
  useGetContractsQuery,
  useGetContractDetailsQuery,
  useGetContractPlannedVolumesQuery,
  useGetContractMeteringPointPlannedVolumesQuery,
  useGetMeteringPointDailyPlannedVolumesQuery,
  useGetContractActsQuery,
  useRequestPlannedVolumeUpdateMutation,
  useCreatePeriodPlannedVolumeMutation,
  useDistributePlannedVolumeMutation,
} = contractsApi;
